import { useContext, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Box, Button, CircularProgress, TextField } from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';

import Autocomplete from '@mui/material/Autocomplete';

import { APIContext } from '../Contexts/APIContext';

type OrgOption = { id: string; organisationName: string };
type SiteOption = { id: string; officeFullName: string;};
type UserOption = { id: string; fullName: string; email: string };

type FiltersResponse = {
    organisations: OrgOption[];
    sites: SiteOption[];
    users: UserOption[];
};

function formatUkDate(d: Dayjs) {
    return d.format('DD/MM/YYYY');
}

function buildQuery(params: Record<string, string | undefined | null>) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    return qs.toString();
}

export default function TelemetryExportPage() {
    const { get } = useContext(APIContext);

    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

    const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
    const [selectedSite, setSelectedSite] = useState<SiteOption | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

    const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
    const [siteOptions, setSiteOptions] = useState<SiteOption[]>([]);
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);

    const [loading, setLoading] = useState(false);

    const userLabel = (u: UserOption) => {
        const name = (u.fullName ?? '').trim();
        const email = (u.email ?? '').trim();
        if (name && email) return `${name} (${email})`;
        if (name) return name;
        if (email) return email;
        return u.id;
    };

    // Load organisations + sites (optionally narrowed by org) from filters endpoint
    const loadOrgAndSiteFilters = (orgId?: string | null) => {
        const qs = buildQuery({ organisationId: orgId ?? undefined });
        const action = qs ? `GetFilters?${qs}` : 'GetFilters';

        setLoading(true);
        get('TelemetryExport', action, (data: FiltersResponse) => {
            setOrgOptions(data.organisations ?? []);
            setSiteOptions(data.sites ?? []);
            // Intentionally NOT overwriting users here, see below.
            setLoading(false);
        });
    };

    // Load full user list once so users can always be searched without org/site selection
    const loadAllUsersOnce = () => {
        setLoading(true);
        get('TelemetryExport', 'GetFilters', (data: FiltersResponse) => {
            setUserOptions(data.users ?? []);
            setLoading(false);
        });
    };

    useEffect(() => {
        // initial load
        loadOrgAndSiteFilters(null);
        loadAllUsersOnce();
    }, []);

    // When org changes: clear site and reload site list for that org
    useEffect(() => {
        setSelectedSite(null);
        loadOrgAndSiteFilters(selectedOrg?.id ?? null);
    }, [selectedOrg?.id]);

    const canDownload = useMemo(() => !!startDate && !!endDate && !endDate.isBefore(startDate, 'day'), [startDate, endDate]);

    const onDownload = () => {
        if (!startDate || !endDate) return;

        const qs = buildQuery({
            from: formatUkDate(startDate),
            to: formatUkDate(endDate),
            organisationId: selectedOrg?.id ?? undefined,
            siteId: selectedSite?.id ?? undefined,
            userId: selectedUser?.id ?? undefined
        });

        // Same-origin: cookies included automatically; server returns Content-Disposition filename
        window.location.href = `api/TelemetryExport/activity.csv?${qs}`;
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Activity Export (CSV)
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(v) => setStartDate(v)}
                            views={['year', 'month', 'day']}
                            openTo="year"
                            maxDate={endDate ?? undefined}
                            disabled={loading}
                        />
                    </Grid>

                    <Grid size={6}>
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(v) => setEndDate(v)}
                            views={['year', 'month', 'day']}
                            openTo="year"
                            minDate={startDate ?? undefined}
                            disabled={loading}
                        />
                    </Grid>

                    <Grid size={12}>
                        <Autocomplete
                            options={orgOptions}
                            value={selectedOrg}
                            onChange={(_, v) => setSelectedOrg(v)}
                            getOptionLabel={(o) => o?.organisationName}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            clearOnEscape
                            renderInput={(params) => <TextField {...params} label="Organisation (optional)" />}
                        />
                    </Grid>

                    <Grid size={12}>
                        <Autocomplete
                            options={siteOptions}
                            value={selectedSite}
                            onChange={(_, v) => setSelectedSite(v)}
                            getOptionLabel={(s)=>s?.officeFullName}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            clearOnEscape
                            renderInput={(params) => <TextField {...params} label="Office (optional)" />}
                        />
                    </Grid>

                    <Grid size={12}>
                        <Autocomplete
                            options={userOptions}
                            value={selectedUser}
                            onChange={(_, v) => setSelectedUser(v)}
                            getOptionLabel={userLabel}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            clearOnEscape
                            // Better search: name + email
                            filterOptions={(options, state) => {
                                const q = state.inputValue.trim().toLowerCase();
                                if (!q) return options;
                                return options.filter(u => {
                                    const name = (u.fullName ?? '').toLowerCase();
                                    const email = (u.email ?? '').toLowerCase();
                                    return name.includes(q) || email.includes(q);
                                });
                            }}
                            renderInput={(params) => <TextField {...params} label="User (optional)" helperText="Search by name or email" />}
                        />
                    </Grid>

                    <Grid size={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button variant="contained" onClick={onDownload} disabled={!canDownload || loading}>
                                Download CSV
                            </Button>

                            {loading && <CircularProgress size={20} />}

                            {!canDownload && (
                                <Typography variant="body2" color="error">
                                    Please pick a valid date range.
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Paper>
    );
}